import { Flex, Text } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  FieldGroupBox,
  RuleStack,
  useDrawerState,
  useFormStateContext,
} from 'ui'
import {
  BuyAndWinBenefitVariant,
  type BuyAndWinBenefit,
} from '../../../../../../typings'
import styles from '../../styles'
import { DEFAULT_BENEFIT, EMPTY_BENEFIT_BY_TYPE } from '../constants'
import { BuyAndWinBenefitEditor } from '../Editor'
import { messages } from '../messages'
import { EmptyBenefit } from './Empty'
import { FreeShippingBenefit } from './FreeShipping'
import { GiftBenefit } from './Gift'
import { MaxUnitPriceBenefit } from './MaxUnitPrice'

type BuyAndWinBenefitViewProps = {
  name: string
  onChange: (config: BuyAndWinBenefit | undefined) => void
}

export type BenefitViewComponentBaseProps<T> = {
  config: T
  onEdit: () => void
  onRemove: () => void
}

const BENEFIT_VIEW_COMPONENTS: {
  [K in BuyAndWinBenefit as K['type']]: React.FC<
    BenefitViewComponentBaseProps<K>
  >
} = {
  [BuyAndWinBenefitVariant.GIFT]: GiftBenefit,
  [BuyAndWinBenefitVariant.FREE_SHIPPING]: FreeShippingBenefit,
  [BuyAndWinBenefitVariant.MAXIMUM_UNIT_PRICE_DISCOUNT]: MaxUnitPriceBenefit,
} as const

export function BuyAndWinBenefitView(props: BuyAndWinBenefitViewProps) {
  const { name, onChange } = props
  const drawerState = useDrawerState()
  const [benefitToEdit, setBenefitToEdit] =
    useState<BuyAndWinBenefit>(DEFAULT_BENEFIT)

  const { form } = useFormStateContext()

  const benefit: BuyAndWinBenefit | undefined = useWatch({ form, name })

  const handleAdd = (type: BuyAndWinBenefitVariant) => {
    setBenefitToEdit(EMPTY_BENEFIT_BY_TYPE[type])

    drawerState.show()
  }

  const getViewContent = () => {
    if (!benefit) return <EmptyBenefit onAdd={handleAdd} name={name} />

    const BenefitComponent = BENEFIT_VIEW_COMPONENTS[benefit.type]

    return (
      <Flex direction="column" className={styles.view.content}>
        <Text>
          <FormattedMessage {...messages.title} />
        </Text>
        <FieldGroupBox name={name} withBorder={false}>
          <RuleStack>
            <BenefitComponent
              onEdit={drawerState.show}
              onRemove={() => onChange(undefined)}
              // @ts-ignore cannot infer type by benefit.type
              config={benefit}
            />
          </RuleStack>
        </FieldGroupBox>
      </Flex>
    )
  }

  return (
    <div>
      {getViewContent()}

      <BuyAndWinBenefitEditor
        config={benefit ?? benefitToEdit}
        state={drawerState}
        onSave={onChange}
      />
    </div>
  )
}
